import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Form, Input, Select, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';

import { useChangeResponsibleMutation, useLazySingleVacationQuery } from 'app/services/edpApiV1/vacations';
import { useSubordinatesQuery } from 'app/services/edpApiV1/viewer';

import { dataObjectForTable, dataObjectForVacationTable } from 'shared/helpers/utils';
import { useNotifications } from 'shared/hooks/use-notifications';

import { useIndicators } from 'features/indicators';
import { TPreparedVacationData, TPreparedVacationTableData } from 'shared/types/vacations.types';
import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';
import { useAppSelector } from 'app/store';
import style from './index.module.scss';

const columns: ColumnsType<TPreparedVacationTableData> = [
  {
    title: '№',
    dataIndex: 'id',
    key: 'id',
    width: 100,
  },
  {
    title: 'Дата',
    dataIndex: 'created',
    key: 'created',
  },
  {
    title: 'ФИО',
    dataIndex: 'fio',
    key: 'fio',
  },
  {
    title: 'Вид отпуска',
    dataIndex: 'vacationType',
    key: 'vacationType',
  },
  {
    title: 'Дата начала',
    dataIndex: 'startedAt',
    key: 'startedAt',
  },
  {
    title: 'Дата окончания',
    dataIndex: 'endedAt',
    key: 'endedAt',
  },
  {
    title: 'Дни',
    dataIndex: 'amountDays',
    key: 'amountDays',
  },
];

const VacationUserSubordination = () => {
  const [dataVacations, setDataVacations] = useState<TPreparedVacationData[] | []>([]);
  const [btnVisible, setBtnVisible] = useState<boolean>(false);
  const tableData = useRef<TPreparedVacationTableData[]>();
  const [form] = Form.useForm();
  const { id } = useParams();
  const vacationHrId = dataVacations?.[0]?.hr?.id;
  const resultAllSubordinates = useSubordinatesQuery({ include: 'me' });
  const { data: allSubordinates } = resultAllSubordinates;
  const [trigger, result] = useChangeResponsibleMutation();
  const [vacationTrigger, { data: vacationData }] = useLazySingleVacationQuery();
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);

  //  храню предыдущее значение id ответственного по отпуску
  const prevHrId = useRef<number | null>(null);
  const { isSuccess, isError, reset } = result;

  const onChangeHr = (currentHrId: number) => {
    if (dataVacations && vacationHrId !== currentHrId) {
      setBtnVisible(true);
    } else {
      setBtnVisible(false);
    }
  };

  const onFinish = (value: { hrId: number }) => {
    const bodyRequest = {
      hrId: value.hrId,
      requestId: id,
    };
    trigger(bodyRequest);
    setBtnVisible(false);
    // задаю предыдущее значение id ответственного по отпуску
    prevHrId.current = value.hrId;
  };

  useEffect(() => {
    if (id) {
      vacationTrigger({ requestId: +id, allAvailableOrganizations });
    }
  }, [id, allAvailableOrganizations]);

  useEffect(() => {
    if (vacationData && id) {
      const dataTable = dataObjectForTable(vacationData);
      const dataForTable = dataObjectForVacationTable(vacationData);
      if (dataForTable) {
        tableData.current = dataForTable;
      }
      if (dataTable) {
        setDataVacations(dataTable);
      }
    }
  }, [vacationData, id]);

  useEffect(() => {
    if (dataVacations && dataVacations?.length > 0) {
      form.setFields([
        {
          name: 'tabel',
          value: dataVacations[0]?.employee?.personnelNumber,
        },
        {
          name: 'division',
          value: dataVacations[0]?.employee?.division,
        },
        {
          name: 'position',
          value: dataVacations[0]?.employee?.position,
        },
        {
          name: 'hrId',
          value: dataVacations[0]?.hr?.id,
        },
      ]);
    }
  }, [dataVacations, form]);
  // инициализирую предыдущее значение id ответственного по отпуску
  useEffect(() => {
    if (prevHrId.current === null && vacationHrId) prevHrId.current = vacationHrId;
  }, [vacationHrId]);

  useIndicators(resultAllSubordinates.isSuccess, resultAllSubordinates.isFetching);
  useNotifications(isSuccess, isError, 'Ответственный изменён', 'Что-то пошло не так', reset);

  return (
    <div className={style.wrap}>
      <Table scroll={{ x: true }} dataSource={tableData.current} columns={columns} pagination={false} bordered />

      <Form form={form} onFinish={onFinish} layout="vertical" name="userInfo" className={style.userInfo}>
        <div className={style.groupItem}>
          <Form.Item name="tabel" label="Табельный номер" labelAlign="left" className={style.inputWrap}>
            <Input className="userInput" disabled />
          </Form.Item>
          <Form.Item name="division" label="Подразделение" labelAlign="left" className={style.inputWrap}>
            <Input className="userInput" disabled />
          </Form.Item>
        </div>
        <div className={style.groupItem}>
          <Form.Item name="position" label="Должность" labelAlign="left" className={style.inputWrap}>
            <Input className="userInput" disabled />
          </Form.Item>
          {allSubordinates && (
            <Form.Item
              name="hrId"
              label="Ответственный HR"
              labelAlign="left"
              className={style.inputWrap}
              rules={[
                {
                  validator: async (_, v) => {
                    if (v === prevHrId.current) return Promise.reject();
                  },
                  message: 'Выберите нового ответственного',
                },
              ]}
            >
              <Select placeholder="Выберите из списка" onChange={onChangeHr}>
                {allSubordinates.map(({ id, name, middleName, lastName }) => (
                  <Select.Option key={id} value={id}>
                    {`${lastName} ${name} ${middleName ?? ''}`}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
        </div>
        {btnVisible && (
          <Form.Item name="hrId" labelAlign="left" className={style.btn}>
            <Button htmlType="submit" type="primary">
              Сохранить
            </Button>
          </Form.Item>
        )}
      </Form>
    </div>
  );
};

export default VacationUserSubordination;
